// Adapted from Sample-Keeper-Pro Layout — simplified for cc-os Wave 1.
// Renders sidebar + header + main content slot. Auth gating happens at the
// route level via Refine's <Authenticated/> wrapper (see App.tsx).
import { useEffect, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { Link, Outlet, useNavigate } from "react-router";
import { HelpCircle, Shield, LogOut, ChevronDown } from "lucide-react";

import { AppSidebar } from "./AppSidebar";
import { Button } from "@/components/ui/button";
import { CountBadge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useBadgeCounts } from "@/hooks/useBadgeCounts";
import { supabase } from "@/lib/supabase";

export function Layout() {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  // Total badge count (#983, Epic #956 2a) next to the user menu — the sum
  // across every surface the user can see, already server-gated (#960).
  // Layout is the SOLE owner of this hook (#1019): it used to also be
  // called independently inside AppSidebar, which is always Layout's direct
  // child, so every authenticated page load fired a second, redundant
  // `get_my_badge_counts()` RPC round trip and a second realtime-channel
  // subscribe attempt. `countFor` is threaded down to AppSidebar as a prop
  // instead.
  const {
    total: badgeTotal,
    countFor,
    realtimeStatus: badgeRealtimeStatus,
  } = useBadgeCounts();

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUser(data.user ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  return (
    <div className="flex h-screen w-full overflow-hidden">
      <AppSidebar countFor={countFor} />

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 flex-shrink-0 items-center justify-between border-b bg-background px-4">
          <h1 className="text-lg font-semibold">CC Staff Portal</h1>
          <div className="flex items-center gap-3">
            {/* Help icon — opens the in-app docs hub. Lives next to the
                user identifier so it's reachable from any screen. */}
            <Link
              to="/docs"
              title="Open the docs (Help)"
              aria-label="Open the docs"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <HelpCircle className="h-4 w-4" />
            </Link>
            {/* User menu — avatar/email dropdown with Account security and
                Sign out. Account security routes to /account/mfa where every
                authenticated user can self-enrol TOTP. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 px-2 text-sm font-normal text-muted-foreground hover:text-foreground"
                  aria-label="User menu"
                >
                  <span className="max-w-[200px] truncate">
                    {user?.email ?? ""}
                  </span>
                  <span data-testid="user-menu-badge">
                    <CountBadge count={badgeTotal} />
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">
                  {user?.email ?? "Signed in"}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => navigate("/account/mfa")}
                  className="cursor-pointer"
                >
                  <Shield className="h-4 w-4" />
                  Account security
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={handleSignOut}
                  className="cursor-pointer"
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Connection state of the badge-count realtime channel. Not
                user-facing (hidden + aria-hidden, so it adds nothing to the
                layout or the a11y tree) — it exists so
                tests/e2e/badge-counts.spec.ts can wait for the
                postgres_changes channel to be JOINED before emitting the
                notification whose live delivery it asserts on. Emitting
                before the join silently drops the event (postgres_changes
                has no replay), which is what made that gating @smoke spec
                fail on PRs that touch nothing related (#1040). Keep the
                testid and the data-status attribute in sync with that spec
                if you move this. */}
            <span
              hidden
              aria-hidden="true"
              data-testid="badge-realtime-status"
              data-status={badgeRealtimeStatus}
            />
          </div>
        </header>

        <main id="app-scroll" className="flex-1 overflow-auto bg-muted/10 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
